import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Image,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import LessonPwoPlayer from "../components/lessonPlans/LessonPwoPlayer";
import LessonVideoPlayer from "../components/lessonPlans/LessonVideoPlayer";
import { apiGetChapterLearningCatalog, apiGetAuthoredChapterPackage } from "./chapterLearningApi";
import AuthoredSoundLesson from "./AuthoredSoundLesson";
import ChapterResourceTile from "./ChapterResourceTile";
import { resourceKey, getResourceTilePreview } from "./chapterResourceTileData";
import { resourceTileColumns, chunkResourceTiles, readResourceVisits, incrementResourceVisit, writeResourceVisits } from "./chapterResourceVisits";

// Opening a tile is browser-local activity, not the platform's authoritative study history.
const browserVisitStorage = () => {
  try { return Platform.OS === "web" && typeof window !== "undefined" ? window.localStorage : null; }
  catch (_) { return null; }
};

const norm = (value) => String(value || "").trim().toLowerCase();

function plainQuestion(raw) {
  if (!raw) return "";
  if (typeof raw !== "string") return String(raw);
  try {
    const parsed = JSON.parse(raw.replace(/'/g, '"'));
    const fields = Array.isArray(parsed?.fields) ? parsed.fields : [];
    return fields
      .filter((field) => field?.field_type === "Text")
      .map((field) => String(field?.field_values || "").replace(/<br\s*\/?\s*>/gi, " "))
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
  } catch {
    const matches = [...raw.matchAll(/field_values['"]?\s*:\s*['"]([^'"]+)/g)];
    if (matches.length) return matches.map((m) => m[1]).join(" ").replace(/\s+/g, " ").trim();
    return raw.replace(/\s+/g, " ").trim();
  }
}

function findTopic(catalog, topicRef) {
  const topics = catalog?.topics || [];
  return (
    topics.find((topic) =>
      topicRef?.topicId != null && Number(topic.topic_id) === Number(topicRef.topicId)
    ) ||
    topics.find((topic) =>
      topicRef?.sourceTopicId != null && Number(topic.source_topic_id) === Number(topicRef.sourceTopicId)
    ) ||
    topics.find((topic) => norm(topic.topic_name) === norm(topicRef?.name)) ||
    null
  );
}

function findObjective(topic, ref) {
  const subtopics = topic?.subtopics || [];
  return (
    subtopics.find((sub) =>
      ref?.subtopicId != null && Number(sub.subtopic_id) === Number(ref.subtopicId)
    ) ||
    subtopics.find((sub) =>
      ref?.sourceSubtopicId != null && Number(sub.source_subtopic_id) === Number(ref.sourceSubtopicId)
    ) ||
    subtopics.find((sub) => norm(sub.subtopic_name) === norm(ref?.name)) ||
    null
  );
}

function uniqueBy(items, keyFn) {
  const seen = new Set();
  const out = [];
  (items || []).forEach((item) => {
    const key = keyFn(item);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(item);
  });
  return out;
}

// Defense-in-depth for legacy records incorrectly marked as English.
// The authoritative language filter is in backend/content_assets.py; reject
// known Gujarati-script mislabels only for English requests.
function matchesQuizLanguage(question, languageId, languageCode) {
  const expected = Number(languageId || 1);
  if (question?.language_id != null && Number(question.language_id) !== expected) return false;
  if (expected === 1 && String(languageCode || "en").toLowerCase().startsWith("en")) {
    const fields = [question?.question, question?.option1, question?.option2,
      question?.option3, question?.option4, question?.correct_ans,
      ...((question?.question_fields || []).map((item) => item?.field_values))];
    if (fields.some((value) => /[\u0A80-\u0AFF]/u.test(String(value || "")))) return false;
  }
  return true;
}

function mergeObjectiveAssets(objectives, assetKey, keyFn) {
  const merged = new Map();
  (objectives || []).forEach((entry) => {
    (entry.node?.assets?.[assetKey] || []).forEach((asset) => {
      const key = keyFn(asset);
      if (!merged.has(key)) {
        merged.set(key, { ...asset, mappedObjectives: [] });
      }
      const current = merged.get(key);
      const objectiveKey = `${entry.node?.subtopic_id ?? entry.ref?.subtopicId ?? ""}|${entry.node?.source_subtopic_id ?? entry.ref?.sourceSubtopicId ?? ""}`;
      if (!current.mappedObjectives.some((item) => item.key === objectiveKey)) {
        current.mappedObjectives.push({
          key: objectiveKey,
          subtopicId: entry.node?.subtopic_id ?? entry.ref?.subtopicId ?? null,
          sourceSubtopicId: entry.node?.source_subtopic_id ?? entry.ref?.sourceSubtopicId ?? null,
          label: cleanObjectiveLabel(entry.node?.subtopic_name || entry.ref?.name || ""),
          tier: entry.tier || "core",
        });
      }
    });
  });
  return Array.from(merged.values());
}

function normalizeAnswer(value) {
  return String(value ?? "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function plainRichFields(fields, fallback = "") {
  if (!Array.isArray(fields) || !fields.length) return plainQuestion(fallback);
  return fields
    .filter((field) => String(field?.field_type || "").toLowerCase() === "text")
    .map((field) => String(field?.field_values || "").replace(/<br\s*\/?\s*>/gi, " "))
    .join(" ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function correctOptionIndex(question, options) {
  const raw = normalizeAnswer(question?.correct_ans);
  if (!raw) return -1;
  if (/^[1-4]$/.test(raw)) return Number(raw) - 1;
  if (/^[a-d]$/.test(raw)) return raw.charCodeAt(0) - "a".charCodeAt(0);
  const optionMatch = raw.match(/^option\s*([1-4])$/);
  if (optionMatch) return Number(optionMatch[1]) - 1;
  const match = options.find((option) => normalizeAnswer(option.label) === raw);
  return match ? match.index : -1;
}

function RichFields({ fields, fallback, textColor, mutedColor }) {
  const normalized = Array.isArray(fields) && fields.length
    ? fields
    : fallback
      ? [{ field_type: "Text", field_values: fallback }]
      : [];

  return (
    <View style={styles.richFields}>
      {normalized.map((field, index) => {
        const type = String(field?.field_type || "Text").toLowerCase();
        const value = String(field?.field_values || "");
        if (!value) return null;
        if (type === "image") {
          return (
            <Image
              key={`image-${index}`}
              source={{ uri: value }}
              resizeMode="contain"
              style={styles.questionImage}
            />
          );
        }
        return (
          <Text key={`text-${index}`} style={[styles.richText, { color: textColor || mutedColor }] }>
            {value.replace(/<br\s*\/?\s*>/gi, " ").replace(/<[^>]+>/g, " ")}
          </Text>
        );
      })}
    </View>
  );
}

function MappedQuizQuestion({ question, index, colors, isDarkMode, t }) {
  const translate = t || ((value) => value);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [typedAnswer, setTypedAnswer] = useState("");
  const [checked, setChecked] = useState(false);
  const [revealed, setRevealed] = useState(false);

  const options = [0, 1, 2, 3]
    .map((optionIndex) => ({
      index: optionIndex,
      raw: question?.[`option${optionIndex + 1}`],
      fields: question?.option_fields?.[optionIndex] || [],
    }))
    .map((option) => ({
      ...option,
      label: plainRichFields(option.fields, option.raw),
    }))
    .filter((option) => option.label || option.fields.length);

  const correctIndex = correctOptionIndex(question, options);
  const hasChoice = options.length >= 2;
  const selectedCorrect = hasChoice
    ? checked && selectedIndex != null && selectedIndex === correctIndex
    : checked && normalizeAnswer(typedAnswer) && normalizeAnswer(typedAnswer) === normalizeAnswer(question?.correct_ans);

  const checkAnswer = () => {
    if (hasChoice && selectedIndex == null) return;
    if (!hasChoice && !typedAnswer.trim()) return;
    setChecked(true);
  };

  return (
    <View style={[styles.quizQuestionCard, { borderColor: colors.border, backgroundColor: colors.panel }] }>
      <View style={styles.quizQuestionHeader}>
        <View style={[styles.questionNumber, { backgroundColor: colors.accentSoft }] }>
          <Text style={[styles.questionNumberText, { color: colors.accent }]}>{index + 1}</Text>
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={[styles.quizMeta, { color: colors.faint }] }>
            {question?.quiz_kind || translate("Practice question")}
            {question?.difficulty_level ? ` · ${translate("Difficulty")} ${question.difficulty_level}` : ""}
          </Text>
          <RichFields
            fields={question?.question_fields}
            fallback={question?.question}
            textColor={colors.text}
            mutedColor={colors.muted}
          />
        </View>
      </View>

      {hasChoice ? (
        <View style={styles.quizOptions}>
          {options.map((option) => {
            const selected = selectedIndex === option.index;
            const showCorrect = (checked || revealed) && option.index === correctIndex;
            const showWrong = checked && selected && correctIndex >= 0 && option.index !== correctIndex;
            return (
              <TouchableOpacity
                key={`option-${option.index}`}
                onPress={() => {
                  setSelectedIndex(option.index);
                  setChecked(false);
                }}
                style={[
                  styles.quizOption,
                  { borderColor: colors.border, backgroundColor: isDarkMode ? "#0f172a" : "#fff" },
                  selected && { borderColor: colors.accent, backgroundColor: colors.accentSoft },
                  showCorrect && { borderColor: colors.success, backgroundColor: isDarkMode ? "rgba(5,150,105,0.12)" : "#ecfdf5" },
                  showWrong && { borderColor: "#dc2626", backgroundColor: isDarkMode ? "rgba(220,38,38,0.12)" : "#fef2f2" },
                ]}
              >
                <View style={[styles.optionLetter, { backgroundColor: selected ? colors.accent : colors.border }] }>
                  <Text style={{ color: selected ? "#fff" : colors.muted, fontWeight: "800", fontSize: 11 }}>
                    {String.fromCharCode(65 + option.index)}
                  </Text>
                </View>
                <View style={{ flex: 1 }}>
                  <RichFields fields={option.fields} fallback={option.raw} textColor={colors.text} mutedColor={colors.muted} />
                </View>
              </TouchableOpacity>
            );
          })}
        </View>
      ) : (
        <TextInput
          value={typedAnswer}
          onChangeText={(value) => {
            setTypedAnswer(value);
            setChecked(false);
          }}
          placeholder={translate("Type your answer")}
          placeholderTextColor={colors.faint}
          style={[styles.answerInput, { color: colors.text, borderColor: colors.border, backgroundColor: isDarkMode ? "#0f172a" : "#fff" }]}
        />
      )}

      <View style={styles.quizActions}>
        <TouchableOpacity
          onPress={checkAnswer}
          style={[styles.quizActionPrimary, { backgroundColor: colors.accent }]}
        >
          <Text style={styles.quizActionPrimaryText}>{translate("Check answer")}</Text>
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => setRevealed((value) => !value)}
          style={[styles.quizActionSecondary, { borderColor: colors.border }]}
        >
          <Text style={[styles.quizActionSecondaryText, { color: colors.muted }]}>
            {revealed ? translate("Hide answer") : translate("Show answer")}
          </Text>
        </TouchableOpacity>
      </View>

      {checked ? (
        <View style={[styles.answerFeedback, { backgroundColor: selectedCorrect ? (isDarkMode ? "rgba(5,150,105,0.12)" : "#ecfdf5") : (isDarkMode ? "rgba(217,119,6,0.12)" : "#fffbeb") }] }>
          <MaterialCommunityIcons
            name={selectedCorrect ? "check-circle-outline" : "information-outline"}
            size={18}
            color={selectedCorrect ? colors.success : colors.warning}
          />
          <Text style={{ flex: 1, color: colors.text, fontSize: 12, lineHeight: 18 }}>
            {selectedCorrect ? translate("Correct.") : translate("Not quite. You can try again or reveal the mapped answer.")}
          </Text>
        </View>
      ) : null}

      {revealed ? (
        <View style={[styles.revealCard, { borderColor: colors.border, backgroundColor: colors.page }] }>
          <Text style={[styles.revealLabel, { color: colors.success }]}>{translate("Mapped answer")}</Text>
          <Text style={[styles.revealAnswer, { color: colors.text }] }>
            {correctIndex >= 0 && options[correctIndex]
              ? options[correctIndex].label
              : String(question?.correct_ans || translate("No answer is stored for this question."))}
          </Text>
          {Array.isArray(question?.hints) && question.hints.length ? (
            <View style={{ marginTop: 10 }}>
              <Text style={[styles.revealLabel, { color: colors.accent }]}>{translate("Hints")}</Text>
              {question.hints.map((hint, hintIndex) => (
                <View key={`hint-${hintIndex}`} style={{ marginTop: 5 }}>
                  <RichFields fields={hint.fields} textColor={colors.muted} mutedColor={colors.muted} />
                </View>
              ))}
            </View>
          ) : null}
          {Array.isArray(question?.solution_fields) && question.solution_fields.length ? (
            <View style={{ marginTop: 10 }}>
              <Text style={[styles.revealLabel, { color: colors.accent }]}>{translate("Solution")}</Text>
              <RichFields fields={question.solution_fields} fallback={question.solution} textColor={colors.muted} mutedColor={colors.muted} />
            </View>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

function cleanObjectiveLabel(name) {
  return String(name || "")
    .replace(/^\s*core\s*-?\s*/i, "")
    .replace(/^\s*suppl?\s*-?\s*/i, "")
    .replace(/^\s*supplement\s*-?\s*/i, "")
    .trim();
}

const DEFAULT_RAIL_WIDTH = 220;
const MIN_RAIL_WIDTH = 190;
const MAX_RAIL_WIDTH = 320;
const RAIL_COLLAPSE_WIDTH = 860;
const RESOURCE_PREVIEW_COUNT = 4;
const QUESTION_PREVIEW_COUNT = 5;

const ChapterLearningWorkspace = ({
  path,
  chapter,
  fetchWithAuth,
  userId,
  authToken,
  languageId = 1,
  localLanguage = "en",
  isDarkMode = false,
  onAsk,
  t,
}) => {
  const translate = t || ((value) => value);
  const { width: windowWidth } = useWindowDimensions();
  const [measuredWidth, setMeasuredWidth] = useState(0);
  // SARAS_CHAPTER_RESOURCE_TILE_V2: grid measures its own available content width.
  const [resourceGridWidth, setResourceGridWidth] = useState(0);
  const tileColumns = resourceTileColumns(resourceGridWidth);
  const tileGap = 14;
  const tileWidth = resourceGridWidth > 0
    ? Math.floor((resourceGridWidth - (tileColumns - 1) * tileGap) / tileColumns)
    : "100%";
  const workspaceWidth = measuredWidth || windowWidth;
  const wide = workspaceWidth >= RAIL_COLLAPSE_WIDTH;
  const [railWidth, setRailWidth] = useState(DEFAULT_RAIL_WIDTH);
  const [railCollapsed, setRailCollapsed] = useState(false);
  const resizeCleanupRef = useRef(null);
  const stageScrollRef = useRef(null);
  const [catalog, setCatalog] = useState(null);
  const [authoredPackage, setAuthoredPackage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tierMode, setTierMode] = useState("extended");
  const [activeKey, setActiveKey] = useState(path?.topics?.[0]?.sections?.[0]?.key || null);
  const [openAsset, setOpenAsset] = useState(null);
  const chapterVisitId = chapter?.chapterId ?? chapter?.id;
  const [resourceVisits, setResourceVisits] = useState(() =>
    readResourceVisits(browserVisitStorage(), userId, chapterVisitId));
  useEffect(() => {
    setResourceVisits(readResourceVisits(browserVisitStorage(), userId, chapterVisitId));
  }, [userId, chapterVisitId]);
  const [expandedPools, setExpandedPools] = useState({ pwos: false, videos: false, quiz: false });

  const colors = {
    page: isDarkMode ? "#0f172a" : "#f8fafc",
    panel: isDarkMode ? "#111827" : "#ffffff",
    rail: isDarkMode ? "#111827" : "#ffffff",
    border: isDarkMode ? "rgba(148,163,184,0.18)" : "#e2e8f0",
    text: isDarkMode ? "#f8fafc" : "#0f172a",
    muted: isDarkMode ? "#94a3b8" : "#64748b",
    faint: isDarkMode ? "#64748b" : "#94a3b8",
    accent: "#6366f1",
    accentSoft: isDarkMode ? "rgba(99,102,241,0.18)" : "#eef2ff",
    core: "#2563eb",
    extended: "#7c3aed",
    success: "#059669",
    warning: "#d97706",
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setCatalog(null);
    setOpenAsset(null);

    apiGetChapterLearningCatalog(fetchWithAuth, {
      chapterId: chapter?.chapterId ?? chapter?.id,
      userId,
      languageId,
      lang: localLanguage,
    })
      .then((data) => {
        if (!cancelled) setCatalog(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || "Could not load chapter learning resources.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [chapter?.chapterId, chapter?.id, fetchWithAuth, userId, languageId, localLanguage]);

  // Optional authored content; existing catalog, PWO, video and quiz paths are unaffected.
  useEffect(() => {
    let cancelled = false;
    setAuthoredPackage(null);
    if (Number(chapter?.chapterId ?? chapter?.id) !== 45) return () => { cancelled = true; };
    apiGetAuthoredChapterPackage(fetchWithAuth, chapter?.chapterId ?? chapter?.id)
      .then((value) => { if (!cancelled) setAuthoredPackage(value); })
      .catch(() => { if (!cancelled) setAuthoredPackage(null); });
    return () => { cancelled = true; };
  }, [chapter?.chapterId, chapter?.id, fetchWithAuth]);

  useEffect(() => {
    setActiveKey(path?.topics?.[0]?.sections?.[0]?.key || null);
    setExpandedPools({ pwos: false, videos: false, quiz: false });
  }, [path?.id]);

  const resolvedSections = useMemo(() => {
    const rows = [];
    (path?.topics || []).forEach((topicDef) => {
      const topic = findTopic(catalog, topicDef.topicRef);
      (topicDef.sections || []).forEach((section) => {
        const objectives = (section.objectives || []).map((ref) => {
          const node = findObjective(topic, ref);
          return {
            ref,
            node,
            tier: node?.tier || ref.tier || "core",
          };
        });
        rows.push({ topicDef, topic, section, objectives });
      });
    });
    return rows;
  }, [path, catalog]);

  const active = resolvedSections.find((row) => row.section.key === activeKey) || resolvedSections[0] || null;

  const sectionData = useMemo(() => {
    if (!active) return null;
    const visibleObjectives = active.objectives.filter(
      (entry) => tierMode === "extended" || entry.tier !== "extended"
    );
    const foundObjectives = visibleObjectives.filter((entry) => entry.node);
    const unresolved = visibleObjectives.filter((entry) => !entry.node);

    const pwos = mergeObjectiveAssets(
      foundObjectives,
      "pwos",
      (item) => `${item.source_pwo_id}|${item.scene_name}`
    );
    const videos = mergeObjectiveAssets(
      foundObjectives,
      "videos",
      (item) => `${item.source_video_id}|${item.tag_id ?? "full"}`
    );
    const quiz = mergeObjectiveAssets(
      foundObjectives,
      "quiz_questions",
      (item) => item.question_id
    ).filter((question) => matchesQuizLanguage(question, languageId, localLanguage));

    return {
      objectives: visibleObjectives,
      unresolved,
      pwos,
      videos,
      quiz,
    };
  }, [active, tierMode, languageId, localLanguage]);

  // One ScrollView is reused for all chapter sections. Reset the viewport when
  // switching the selected section or Core/Extended tier; never inherit quiz scroll.
  useEffect(() => {
    stageScrollRef.current?.scrollTo({ y: 0, animated: false });
  }, [activeKey, tierMode]);

  const selectSection = (key) => {
    setActiveKey(key);
    setOpenAsset(null);
    setExpandedPools({ pwos: false, videos: false, quiz: false });
  };

  const togglePool = (key) => {
    // Closing an expanded pool must never strand a hidden active player.
    setOpenAsset(null);
    setExpandedPools((current) => ({ ...current, [key]: !current[key] }));
  };

  const handleResourceTileOpen = (kind, asset) => {
    const key = resourceKey(kind, asset);
    if (openAsset?.key === key) { setOpenAsset(null); return; }
    setOpenAsset({ key, kind, asset });
    // Count actual opens only, not card impressions or close actions.
    const nextVisits = incrementResourceVisit(resourceVisits, key);
    setResourceVisits(nextVisits);
    writeResourceVisits(browserVisitStorage(), userId, chapterVisitId, nextVisits);
  };

  const beginRailResize = useCallback((event) => {
    if (Platform.OS !== "web" || railCollapsed) return;
    const clientX = event?.nativeEvent?.clientX ?? event?.clientX;
    if (!Number.isFinite(clientX) || typeof window === "undefined") return;

    event?.preventDefault?.();
    const startX = clientX;
    const startWidth = railWidth;

    const onMove = (moveEvent) => {
      const next = Math.max(
        MIN_RAIL_WIDTH,
        Math.min(MAX_RAIL_WIDTH, startWidth + (moveEvent.clientX - startX))
      );
      setRailWidth(next);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      resizeCleanupRef.current = null;
      if (typeof document !== "undefined") document.body.style.cursor = "";
    };

    resizeCleanupRef.current = onUp;
    if (typeof document !== "undefined") document.body.style.cursor = "col-resize";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [railCollapsed, railWidth]);

  useEffect(() => () => {
    resizeCleanupRef.current?.();
  }, []);

  if (loading) {
    return (
      <View style={[styles.centerState, { backgroundColor: colors.page }]}>
        <ActivityIndicator size="large" color={colors.accent} />
        <Text style={[styles.stateText, { color: colors.muted }]}>{translate("Loading learning path…")}</Text>
      </View>
    );
  }

  if (error || !catalog || !active || !sectionData) {
    return (
      <View style={[styles.centerState, { backgroundColor: colors.page }]}>
        <MaterialCommunityIcons name="alert-circle-outline" size={28} color={colors.warning} />
        <Text style={[styles.stateTitle, { color: colors.text }]}>{translate("Learning path unavailable")}</Text>
        <Text style={[styles.stateText, { color: colors.muted }]}>{error || translate("This chapter could not be resolved against the current curriculum database.")}</Text>
      </View>
    );
  }

  const renderRail = () => (
    <ScrollView
      style={styles.railScroll}
      contentContainerStyle={styles.railContent}
      showsVerticalScrollIndicator
    >
      <View style={styles.railHeadingRow}>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={[styles.railEyebrow, { color: colors.accent }]}>{translate("LEARNING PATH")}</Text>
          <Text style={[styles.railChapter, { color: colors.text }]}>{path.title}</Text>
        </View>
        <TouchableOpacity
          onPress={() => setRailCollapsed(true)}
          accessibilityRole="button"
          accessibilityLabel={translate("Collapse learning path")}
          style={[styles.railCollapseButton, { borderColor: colors.border }]}
        >
          <MaterialCommunityIcons name="chevron-left" size={18} color={colors.muted} />
        </TouchableOpacity>
      </View>
      {(path.topics || []).map((topicDef) => (
        <View key={topicDef.key} style={styles.railTopic}>
          <Text style={[styles.railTopicTitle, { color: colors.muted }]}>{topicDef.title}</Text>
          {(topicDef.sections || []).map((section, index) => {
            const selected = section.key === active.section.key;
            return (
              <TouchableOpacity
                key={section.key}
                onPress={() => selectSection(section.key)}
                style={[
                  styles.railSection,
                  selected && { backgroundColor: colors.accentSoft, borderColor: colors.accent },
                  !selected && { borderColor: "transparent" },
                ]}
              >
                <View style={[styles.railIndex, { backgroundColor: selected ? colors.accent : colors.border }]}>
                  <Text style={[styles.railIndexText, { color: selected ? "#fff" : colors.muted }]}>{index + 1}</Text>
                </View>
                <Text style={[styles.railSectionText, { color: selected ? colors.text : colors.muted }]}>{section.title}</Text>
              </TouchableOpacity>
            );
          })}
        </View>
      ))}
    </ScrollView>
  );

  // SARAS_CHAPTER_RESOURCE_TILE_V1: visual presentation only; exact resource identities are unchanged.
  const renderResourceTile = (kind, asset) => {
    const key = resourceKey(kind, asset);
    const preview = getResourceTilePreview(kind, asset, authoredPackage?.resource_preview_catalog);
    const selected = openAsset?.key === key;
    return (
      <View key={key} style={[styles.resourceTileShell, { width: tileWidth }]}>
        <ChapterResourceTile
          kind={kind}
          asset={asset}
          visitCount={resourceVisits[key] || 0}
          imageUri={preview.uri}
          description={preview.description}
          selected={selected}
          isDarkMode={isDarkMode}
          t={t}
          onPress={() => handleResourceTileOpen(kind, asset)}
        />
      </View>
    );
  };

  // Insert the player immediately BELOW ITS TILE ROW, never at the bottom of all resources.
  // This keeps the selected tile and the open/close scroll position near each other.
  const renderResourceGrid = (kind, assets) => (
    <View
      style={styles.resourceTileGrid}
      onLayout={(event) => {
        const actualWidth = Math.floor(event.nativeEvent.layout.width);
        if (actualWidth > 0) setResourceGridWidth((before) => before === actualWidth ? before : actualWidth);
      }}
    >
      {chunkResourceTiles(assets, tileColumns).map((row, index) => (
        <View key={`${kind}-row-${index}`} style={styles.resourceTileRow}>
          <View style={styles.resourceTileRowCards}>
            {row.map((asset) => renderResourceTile(kind, asset))}
          </View>
          {row.some((asset) => openAsset?.kind === kind && openAsset?.key === resourceKey(kind, asset))
            ? renderOpenedResource(kind) : null}
        </View>
      ))}
    </View>
  );

  // Player receives the exact original source IDs. Full width avoids narrow inline embeds.
  const renderOpenedResource = (kind) => {
    if (!openAsset || openAsset.kind !== kind) return null;
    const asset = openAsset.asset;
    return (
      <View style={[styles.resourceOpenPanel, { backgroundColor: colors.panel, borderColor: colors.border }]}>
        <View style={styles.resourceOpenHeader}>
          <MaterialCommunityIcons name={kind === "pwo" ? "cube-outline" : "play-circle-outline"} size={19} color={colors.accent} />
          <Text style={[styles.resourceOpenTitle, { color: colors.text }]}>
            {kind === "pwo" ? translate("Exploring in 3D") : translate("Watching")}
          </Text>
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel={translate("Close")}
            onPress={() => setOpenAsset(null)}
            style={[styles.openButton, { backgroundColor: colors.accentSoft }]}
          >
            <Text style={[styles.openButtonText, { color: colors.accent }]}>{translate("Close")}</Text>
          </TouchableOpacity>
        </View>
        {kind === "pwo" ? (
          <LessonPwoPlayer
            key={openAsset.key}
            sourcePwoId={asset.source_pwo_id}
            sceneName={asset.scene_name}
            authToken={authToken}
            localLanguage={localLanguage}
            isDarkMode={isDarkMode}
            onClose={() => setOpenAsset(null)}
            t={t}
          />
        ) : (
          <LessonVideoPlayer
            key={openAsset.key}
            sourceVideoId={asset.source_video_id}
            tagId={asset.tag_id}
            authToken={authToken}
            languageId={languageId}
            isDarkMode={isDarkMode}
            onClose={() => setOpenAsset(null)}
            t={t}
          />
        )}
      </View>
    );
  };

  return (
    <View
      style={[styles.container, { backgroundColor: colors.page }]}
      onLayout={(event) => setMeasuredWidth(event.nativeEvent.layout.width)}
    >
      <View style={[styles.header, { backgroundColor: colors.panel, borderColor: colors.border }]}>
        <View style={{ flex: 1 }}>
          <Text style={[styles.headerEyebrow, { color: colors.accent }]}>{chapter?.subjectName || path.subject}</Text>
          <Text style={[styles.headerTitle, { color: colors.text }]}>{path.title}</Text>
          <Text style={[styles.headerSubtitle, { color: colors.muted }]}>{path.subtitle}</Text>
        </View>
        <View style={[styles.tierSwitch, { borderColor: colors.border }]}>
          {[{ key: "core", label: "Core" }, { key: "extended", label: "Extended" }].map((mode) => {
            const selected = tierMode === mode.key;
            return (
              <TouchableOpacity
                key={mode.key}
                onPress={() => {
                  setTierMode(mode.key);
                  setOpenAsset(null);
                  setExpandedPools({ pwos: false, videos: false, quiz: false });
                }}
                style={[styles.tierButton, selected && { backgroundColor: colors.accent }]}
              >
                <Text style={[styles.tierButtonText, { color: selected ? "#fff" : colors.muted }]}>{translate(mode.label)}</Text>
              </TouchableOpacity>
            );
          })}
        </View>
      </View>

      <View style={[styles.body, !wide && styles.bodyNarrow]}>
        {wide && !railCollapsed ? (
          <>
            <View
              style={[
                styles.railShell,
                {
                  width: railWidth,
                  backgroundColor: colors.rail,
                  borderColor: colors.border,
                },
              ]}
            >
              {renderRail()}
            </View>
            <View
              onMouseDown={beginRailResize}
              style={styles.railResizeHandle}
              accessibilityRole="adjustable"
              accessibilityLabel={translate("Resize learning path")}
            >
              <View style={[styles.railResizeGrip, { backgroundColor: colors.border }]} />
            </View>
          </>
        ) : null}

        {wide && railCollapsed ? (
          <TouchableOpacity
            onPress={() => setRailCollapsed(false)}
            accessibilityRole="button"
            accessibilityLabel={translate("Show learning path")}
            style={[styles.railExpandButton, { backgroundColor: colors.panel, borderColor: colors.border }]}
          >
            <MaterialCommunityIcons name="format-list-bulleted" size={18} color={colors.accent} />
            <MaterialCommunityIcons name="chevron-right" size={16} color={colors.muted} />
          </TouchableOpacity>
        ) : null}

        <View style={[styles.stage, { backgroundColor: colors.panel, borderColor: colors.border }]}>
          {!wide ? (
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.mobileRail} contentContainerStyle={styles.mobileRailContent}>
              {resolvedSections.map((row) => (
                <TouchableOpacity
                  key={row.section.key}
                  onPress={() => selectSection(row.section.key)}
                  style={[styles.mobileRailChip, { borderColor: row.section.key === active.section.key ? colors.accent : colors.border }]}
                >
                  <Text style={{ color: row.section.key === active.section.key ? colors.accent : colors.muted, fontWeight: "600" }}>{row.section.title}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          ) : null}

          <ScrollView ref={stageScrollRef} contentContainerStyle={styles.stageContent} showsVerticalScrollIndicator={false}>
            <Text style={[styles.topicEyebrow, { color: colors.accent }]}>{active.topicDef.title}</Text>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>{active.section.title}</Text>

            {authoredPackage?.status !== "published" && authoredPackage?.sections?.some(s => s.navigation_key === active.section.key) ? (
              <AuthoredSoundLesson
                sections={authoredPackage.sections.filter(s => s.navigation_key === active.section.key)
                  .filter(s => tierMode === "extended" || /\bcore\b/i.test(String(s.tier || "")))}
                topicLabel={active.topicDef.title}
                colors={colors}
                isDarkMode={isDarkMode}
              />
            ) : null}

            <View style={[styles.learningOverviewCard, { backgroundColor: colors.page, borderColor: colors.border }]}>
              <View style={styles.learningOverviewHeader}>
                <MaterialCommunityIcons name="target" size={21} color={colors.accent} />
                <Text style={[styles.blockTitle, { color: colors.text }]}>{translate("What you'll learn")}</Text>
              </View>
              <Text style={[styles.intro, { color: colors.muted }]}>{active.section.intro}</Text>

              <Text style={[styles.overviewSubheading, { color: colors.text }]}>{translate("Key ideas")}</Text>
              <View style={styles.keyIdeas}>
                {(active.section.keyIdeas || []).map((idea) => (
                  <View key={idea} style={styles.keyIdeaRow}>
                    <MaterialCommunityIcons name="check-circle-outline" size={18} color={colors.success} />
                    <Text style={[styles.keyIdeaText, { color: colors.text }]}>{idea}</Text>
                  </View>
                ))}
              </View>
            </View>

            <View style={[styles.coverageCard, { backgroundColor: colors.page, borderColor: colors.border }]}>
              <View style={styles.learningOverviewHeader}>
                <MaterialCommunityIcons name="book-open-variant" size={19} color={colors.accent} />
                <Text style={[styles.blockTitle, { color: colors.text }]}>{translate("Curriculum goals")}</Text>
              </View>
              <Text style={[styles.blockCaption, { color: colors.muted }]}>
                {translate("These Cambridge learning goals are covered in this section.")}
              </Text>
              <View style={styles.objectiveWrap}>
                {sectionData.objectives.map(({ ref, node, tier }) => (
                  <View
                    key={`${ref.subtopicId}-${ref.name}`}
                    style={[
                      styles.objectivePill,
                      {
                        borderColor: node ? colors.border : colors.warning,
                        backgroundColor: tier === "extended" ? (isDarkMode ? "rgba(124,58,237,0.14)" : "#f5f3ff") : (isDarkMode ? "rgba(37,99,235,0.12)" : "#eff6ff"),
                      },
                    ]}
                  >
                    <Text style={{ color: tier === "extended" ? colors.extended : colors.core, fontWeight: "700", fontSize: 11 }}>
                      {tier === "extended" ? translate("Extended") : translate("Core")}
                    </Text>
                    <Text style={[styles.objectiveText, { color: colors.text }]}>{cleanObjectiveLabel(node?.subtopic_name || ref.name)}</Text>
                  </View>
                ))}
              </View>
              {sectionData.unresolved.length ? (
                <Text style={[styles.unresolved, { color: colors.warning }]}>⚠ {sectionData.unresolved.length} mapped objective(s) did not resolve in the current database.</Text>
              ) : null}
            </View>

            <View style={styles.sectionBlock}>
              <Text style={[styles.blockTitle, { color: colors.text }]}>{translate("Explore and learn")}</Text>
              <Text style={[styles.blockCaption, { color: colors.muted }] }>
                {translate("Use the 3D explorations and videos below to build and reinforce the ideas in this section. All available mapped resources are included.")}
              </Text>

              <View style={styles.resourceGroup}>
                <View style={styles.resourceGroupHeader}>
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.resourceGroupTitle, { color: colors.text }]}>{translate("Explore in 3D")}</Text>
                    <Text style={[styles.resourceGroupCount, { color: colors.faint }]}>{sectionData.pwos.length} {translate("3D exploration(s)")}</Text>
                  </View>
                  {sectionData.pwos.length > RESOURCE_PREVIEW_COUNT ? (
                    <TouchableOpacity onPress={() => togglePool("pwos")} style={[styles.showAllButton, { borderColor: colors.border }]}>
                      <Text style={[styles.showAllText, { color: colors.accent }]}>
                        {expandedPools.pwos ? translate("Show fewer") : `${translate("Show all")} (${sectionData.pwos.length})`}
                      </Text>
                    </TouchableOpacity>
                  ) : null}
                </View>
                {renderResourceGrid("pwo", expandedPools.pwos
                  ? sectionData.pwos : sectionData.pwos.slice(0, RESOURCE_PREVIEW_COUNT))}
                {!sectionData.pwos.length ? (
                  <Text style={[styles.emptyText, { color: colors.muted }]}>{translate("No mapped 3D scene is available for this section yet.")}</Text>
                ) : null}
              </View>

              <View style={styles.resourceGroup}>
                <View style={styles.resourceGroupHeader}>
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.resourceGroupTitle, { color: colors.text }]}>{translate("Watch")}</Text>
                    <Text style={[styles.resourceGroupCount, { color: colors.faint }]}>{sectionData.videos.length} {translate("video(s)")}</Text>
                  </View>
                  {sectionData.videos.length > RESOURCE_PREVIEW_COUNT ? (
                    <TouchableOpacity onPress={() => togglePool("videos")} style={[styles.showAllButton, { borderColor: colors.border }]}>
                      <Text style={[styles.showAllText, { color: colors.accent }]}>
                        {expandedPools.videos ? translate("Show fewer") : `${translate("Show all")} (${sectionData.videos.length})`}
                      </Text>
                    </TouchableOpacity>
                  ) : null}
                </View>
                {renderResourceGrid("video", expandedPools.videos
                  ? sectionData.videos : sectionData.videos.slice(0, RESOURCE_PREVIEW_COUNT))}
                {!sectionData.videos.length ? (
                  <Text style={[styles.emptyText, { color: colors.muted }]}>{translate("No mapped video is available for this section yet.")}</Text>
                ) : null}
              </View>
            </View>

            <View style={[styles.practiceCard, { backgroundColor: colors.page, borderColor: colors.border }] }>
              <View style={styles.practiceHeader}>
                <MaterialCommunityIcons name="help-circle-outline" size={21} color={colors.accent} />
                <View style={{ flex: 1 }}>
                  <Text style={[styles.blockTitle, { color: colors.text }]}>{translate("Practice")}</Text>
                  <Text style={[styles.blockCaption, { color: colors.muted }] }>
                    {sectionData.quiz.length} {translate("practice question(s) available for this section. Use Show all to view the complete set.")}
                  </Text>
                </View>
                {sectionData.quiz.length > QUESTION_PREVIEW_COUNT ? (
                  <TouchableOpacity onPress={() => togglePool("quiz")} style={[styles.showAllButton, { borderColor: colors.border }]}>
                    <Text style={[styles.showAllText, { color: colors.accent }]}>
                      {expandedPools.quiz ? translate("Show fewer") : `${translate("Show all")} (${sectionData.quiz.length})`}
                    </Text>
                  </TouchableOpacity>
                ) : null}
              </View>

              {(expandedPools.quiz ? sectionData.quiz : sectionData.quiz.slice(0, QUESTION_PREVIEW_COUNT)).map((question, index) => (
                <MappedQuizQuestion
                  key={question.question_id}
                  question={question}
                  index={index}
                  colors={colors}
                  isDarkMode={isDarkMode}
                  t={t}
                />
              ))}
              {!sectionData.quiz.length ? (
                <Text style={[styles.emptyText, { color: colors.muted }]}>{translate("No mapped quiz question is available for this section yet.")}</Text>
              ) : null}
            </View>

            <TouchableOpacity
              onPress={() => onAsk?.({ path, topic: active.topicDef, section: active.section })}
              style={[styles.askButton, { backgroundColor: colors.accent }]}
            >
              <MaterialCommunityIcons name="star-four-points-outline" size={18} color="#fff" />
              <Text style={styles.askButtonText}>{translate("Ask Saras about this section")}</Text>
            </TouchableOpacity>
          </ScrollView>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, minHeight: 0, paddingHorizontal: 12, paddingBottom: 100, gap: 10 },
  header: { borderWidth: 1, borderRadius: 16, paddingHorizontal: 20, paddingVertical: 16, flexDirection: "row", alignItems: "center", gap: 18 },
  headerEyebrow: { fontSize: 11, fontWeight: "800", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 3 },
  headerTitle: { fontSize: 23, fontWeight: "800" },
  headerSubtitle: { fontSize: 13, marginTop: 4, maxWidth: 760, lineHeight: 19 },
  tierSwitch: { flexDirection: "row", borderWidth: 1, borderRadius: 10, padding: 3 },
  tierButton: { paddingVertical: 7, paddingHorizontal: 12, borderRadius: 7 },
  tierButtonText: { fontSize: 12, fontWeight: "700" },
  body: { flex: 1, minHeight: 560, flexDirection: "row", gap: 0 },
  bodyNarrow: { flexDirection: "column" },
  railShell: { flexGrow: 0, flexShrink: 0, minWidth: 0, borderWidth: 1, borderRadius: 16, overflow: "hidden" },
  railScroll: { flex: 1, minHeight: 0 },
  railContent: { padding: 12, paddingBottom: 30 },
  railHeadingRow: { flexDirection: "row", alignItems: "flex-start", gap: 8 },
  railCollapseButton: { width: 30, height: 30, borderWidth: 1, borderRadius: 8, alignItems: "center", justifyContent: "center" },
  railResizeHandle: { width: 12, flexShrink: 0, alignItems: "center", justifyContent: "center", cursor: "col-resize" },
  railResizeGrip: { width: 3, height: 46, borderRadius: 999 },
  railExpandButton: { width: 42, flexShrink: 0, borderWidth: 1, borderRadius: 12, marginRight: 8, alignItems: "center", justifyContent: "center", gap: 2 },
  railEyebrow: { fontSize: 10, fontWeight: "800", letterSpacing: 0.8 },
  railChapter: { fontSize: 18, fontWeight: "800", marginTop: 3, marginBottom: 14 },
  railTopic: { marginBottom: 15 },
  railTopicTitle: { fontSize: 11, fontWeight: "800", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 5 },
  railSection: { borderWidth: 1, borderRadius: 10, paddingHorizontal: 8, paddingVertical: 8, flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 4 },
  railIndex: { width: 24, height: 24, borderRadius: 12, alignItems: "center", justifyContent: "center" },
  railIndexText: { fontSize: 11, fontWeight: "800" },
  railSectionText: { flex: 1, fontSize: 12, fontWeight: "600", lineHeight: 16 },
  stage: { flex: 1, minWidth: 0, borderWidth: 1, borderRadius: 16, overflow: "hidden" },
  stageContent: { padding: 20, paddingBottom: 80 },
  learningOverviewCard: { borderWidth: 1, borderRadius: 14, padding: 16, marginTop: 18 },
  learningOverviewHeader: { flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 6 },
  overviewSubheading: { fontSize: 13, fontWeight: "800", marginTop: 14 },
  mobileRail: { maxHeight: 54, borderBottomWidth: 1, borderBottomColor: "#e2e8f0" },
  mobileRailContent: { padding: 8, gap: 8 },
  mobileRailChip: { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 999, borderWidth: 1 },
  topicEyebrow: { fontSize: 11, fontWeight: "800", textTransform: "uppercase", letterSpacing: 0.7 },
  sectionTitle: { fontSize: 28, fontWeight: "800", marginTop: 4 },
  intro: { fontSize: 15, lineHeight: 23, marginTop: 4, maxWidth: 900 },
  keyIdeas: { marginTop: 9, gap: 9 },
  keyIdeaRow: { flexDirection: "row", alignItems: "flex-start", gap: 8 },
  keyIdeaText: { flex: 1, fontSize: 14, lineHeight: 20 },
  coverageCard: { marginTop: 22, borderWidth: 1, borderRadius: 14, padding: 16 },
  blockEyebrow: { fontSize: 10, fontWeight: "800", letterSpacing: 0.8, marginBottom: 10 },
  objectiveWrap: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
  objectivePill: { borderWidth: 1, borderRadius: 12, paddingHorizontal: 11, paddingVertical: 8, maxWidth: 360 },
  objectiveText: { fontSize: 12, fontWeight: "600", marginTop: 2 },
  unresolved: { fontSize: 12, marginTop: 10, fontWeight: "600" },
  sectionBlock: { marginTop: 24 },
  blockTitle: { fontSize: 17, fontWeight: "800" },
  blockCaption: { fontSize: 12, lineHeight: 18, marginTop: 4, marginBottom: 10 },
  assetCard: { borderWidth: 1, borderRadius: 14, marginTop: 10, overflow: "hidden" },
  assetHeader: { padding: 12, flexDirection: "row", alignItems: "center", gap: 10 },
  assetIcon: { width: 38, height: 38, borderRadius: 10, alignItems: "center", justifyContent: "center" },
  assetKind: { fontSize: 9, fontWeight: "800", letterSpacing: 0.7 },
  assetTitle: { fontSize: 13, fontWeight: "700", marginTop: 2 },
  assetCoverage: { fontSize: 10, marginTop: 3 },
  openButton: { paddingHorizontal: 12, paddingVertical: 7, borderRadius: 8 },
  openButtonText: { fontSize: 12, fontWeight: "800" },
  inlinePlayer: { borderTopWidth: 1, borderTopColor: "#e2e8f0", padding: 10, minHeight: 280 },
  moreText: { fontSize: 11, marginTop: 9 },
  emptyText: { fontSize: 13, paddingVertical: 12 },
  resourceTileGrid: { width: "100%", marginTop: 12 },
  resourceTileRow: { width: "100%", marginBottom: 14 },
  resourceTileRowCards: { flexDirection: "row", alignItems: "stretch", gap: 14 },
  resourceTileShell: { flexShrink: 0, minWidth: 0 },
  resourceOpenPanel: { borderWidth: 1, borderRadius: 14, marginTop: 16, padding: 12, minHeight: 310 },
  resourceOpenHeader: { flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 12 },
  resourceOpenTitle: { flex: 1, fontSize: 15, fontWeight: "800" },
  resourceGroup: { marginTop: 18 },
  resourceGroupHeader: { flexDirection: "row", alignItems: "center", gap: 10, marginBottom: 2 },
  resourceGroupTitle: { fontSize: 14, fontWeight: "800" },
  resourceGroupCount: { fontSize: 11, marginTop: 2 },
  showAllButton: { borderWidth: 1, borderRadius: 9, paddingHorizontal: 10, paddingVertical: 7 },
  showAllText: { fontSize: 11, fontWeight: "800" },
  practiceCard: { borderWidth: 1, borderRadius: 14, padding: 16, marginTop: 24 },
  practiceHeader: { flexDirection: "row", alignItems: "flex-start", gap: 10 },
  quizQuestionCard: { borderWidth: 1, borderRadius: 13, padding: 14, marginTop: 12 },
  quizQuestionHeader: { flexDirection: "row", alignItems: "flex-start", gap: 10 },
  questionNumber: { width: 28, height: 28, borderRadius: 14, alignItems: "center", justifyContent: "center" },
  questionNumberText: { fontSize: 12, fontWeight: "800" },
  quizMeta: { fontSize: 10, fontWeight: "700", marginBottom: 5 },
  richFields: { gap: 6 },
  richText: { fontSize: 13, lineHeight: 20 },
  questionImage: { width: "100%", minHeight: 90, maxHeight: 260, marginVertical: 4 },
  quizOptions: { marginTop: 12, gap: 8 },
  quizOption: { borderWidth: 1, borderRadius: 10, padding: 10, flexDirection: "row", alignItems: "flex-start", gap: 9 },
  optionLetter: { width: 24, height: 24, borderRadius: 12, alignItems: "center", justifyContent: "center", marginTop: 1 },
  answerInput: { borderWidth: 1, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 10, fontSize: 13, marginTop: 12 },
  quizActions: { flexDirection: "row", flexWrap: "wrap", gap: 8, marginTop: 12 },
  quizActionPrimary: { borderRadius: 9, paddingHorizontal: 12, paddingVertical: 8 },
  quizActionPrimaryText: { color: "#fff", fontSize: 12, fontWeight: "800" },
  quizActionSecondary: { borderWidth: 1, borderRadius: 9, paddingHorizontal: 12, paddingVertical: 8 },
  quizActionSecondaryText: { fontSize: 12, fontWeight: "700" },
  answerFeedback: { borderRadius: 10, padding: 10, marginTop: 10, flexDirection: "row", gap: 8, alignItems: "flex-start" },
  revealCard: { borderWidth: 1, borderRadius: 10, padding: 11, marginTop: 10 },
  revealLabel: { fontSize: 10, fontWeight: "800", textTransform: "uppercase", letterSpacing: 0.6 },
  revealAnswer: { fontSize: 13, fontWeight: "700", marginTop: 4, lineHeight: 19 },
  askButton: { alignSelf: "flex-start", marginTop: 20, borderRadius: 10, paddingHorizontal: 15, paddingVertical: 10, flexDirection: "row", alignItems: "center", gap: 8 },
  askButtonText: { color: "#fff", fontWeight: "800", fontSize: 13 },
  centerState: { flex: 1, minHeight: 420, alignItems: "center", justifyContent: "center", padding: 30, gap: 10 },
  stateTitle: { fontSize: 18, fontWeight: "800" },
  stateText: { fontSize: 13, textAlign: "center", maxWidth: 520 },
});

export default ChapterLearningWorkspace;
